import { PbkHeaders } from '@pbk-web/data';
import { useCurrentClient, useMyClients } from '@pbk-web/state';
import type { FCWithChildren } from '@pbk-web/types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useWindowFocused } from '../hooks/useWindowFocused';
import type { NotificationResponse } from '../models/Notification';
import BaseService from '../services/BaseService';
import NotificationService from '../services/NotificationService';
import { usePbkAuth } from './PbkAuthContext';

type ContextType = {
  latestUnreadNotifs: NotificationResponse[];
  totalCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  isLoadingNotifs: boolean;
  showNotifications: boolean;
};

const Context = createContext<ContextType>(null!);

export const useLatestNotificationsProvider = () => useContext(Context);

export const LatestNotificationsProvider: FCWithChildren = (props) => {
  const { children } = props;
  const [isLoadingNotifs, setIsLoadingNotifs] = useState(true);
  const [latestUnreadNotifs, setLatestUnreadNotifs] = useState<NotificationResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const currentClient = useCurrentClient((x) => x.value);
  const myClients = useMyClients((x) => x.value);
  const { isAuthenticated } = usePbkAuth();

  const showNotifications = useMemo(() => {
    const hasMultipleTenants = [...new Set(myClients.map((x) => x.tenantId))].length > 1;
    return !!currentClient || (!currentClient && !hasMultipleTenants);
  }, [currentClient, myClients]);

  const isWindowFocused = useWindowFocused();
  const prevIsWindowFocused = useRef(isWindowFocused);

  const initialLoadRef = useRef(true);

  const loadLatestUnread = useCallback(() => {
    if (
      showNotifications &&
      (initialLoadRef.current || (isWindowFocused && prevIsWindowFocused.current)) &&
      !!isAuthenticated &&
      !!BaseService.httpClient.defaults.headers[PbkHeaders.ClientId]
    ) {
      setIsLoadingNotifs(true);
      NotificationService.getNotifications({
        isRead: false,
        pageNumber: 1,
        pageSize: 25,
      }).then((res) => {
        setLatestUnreadNotifs(res.data);
        setTotalCount(res.totalCount ?? 0);
        setIsLoadingNotifs(false);
        initialLoadRef.current = false;
      });
    }

    prevIsWindowFocused.current = isWindowFocused;
  }, [isAuthenticated, isWindowFocused, showNotifications]);

  useEffect(
    function refreshLatestUnread() {
      if (!showNotifications) {
        setTotalCount(0);
      }

      loadLatestUnread();
      const interval = setInterval(loadLatestUnread, 60 * 1000); // every 60 seconds
      return () => clearInterval(interval);
    },
    [currentClient?.id, loadLatestUnread, showNotifications],
  );

  const markAsRead = useCallback((id: string) => {
    NotificationService.updateNotification({ ids: [id], markAsRead: true });
    setLatestUnreadNotifs((prev) => prev.filter((x) => x.id !== id));
    setTotalCount((prev) => prev - 1);
  }, []);

  const markAllAsRead = useCallback(() => {
    NotificationService.markAllAsRead();
    setLatestUnreadNotifs([]);
    setTotalCount(0);
  }, []);

  const values = useMemo<ContextType>(
    () => ({
      latestUnreadNotifs,
      totalCount,
      markAsRead,
      markAllAsRead,
      isLoadingNotifs,
      showNotifications,
    }),
    [showNotifications, isLoadingNotifs, latestUnreadNotifs, markAllAsRead, markAsRead, totalCount],
  );

  return <Context.Provider value={values}>{children}</Context.Provider>;
};
